import { CommonModule, CurrencyPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog'
import {
  ClinicService,
  EstimateHistoryModel,
  getLogTypeName,
  getTimeZone,
  LOG_TYPE,
} from '@mmx/shared'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { groupBy } from 'lodash'
import moment from 'moment-timezone'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { catchError, finalize, map, shareReplay } from 'rxjs/operators'

import { HistoryDay, HistoryDialogModule } from '../../../history-dialog'
import { EstimateBalanceApiService } from '../../services/estimate-balance-api.service'
import { describeEstimateHistoryEntry } from './describe-history-entry'

export interface BalanceHistoryDialogData {
  estimateId: string
}

@Component({
  selector: 'mmx-balance-history-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, TranslateModule, HistoryDialogModule],
  providers: [CurrencyPipe],
  templateUrl: './balance-history-dialog.component.html',
})
export class BalanceHistoryDialogComponent implements OnInit, OnDestroy {
  // Subjects kept private so external callers cannot push values; templates
  // and child components read the observable views. See M18.
  private readonly loadingSubject = new BehaviorSubject<boolean>(true)
  private readonly errorSubject = new BehaviorSubject<boolean>(false)
  readonly loading$: Observable<boolean> = this.loadingSubject.asObservable()
  readonly error$: Observable<boolean> = this.errorSubject.asObservable()
  history$!: Observable<HistoryDay<EstimateHistoryModel>[]>
  readonly tz: string

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: BalanceHistoryDialogData,
    private readonly api: EstimateBalanceApiService,
    private readonly currency: CurrencyPipe,
    private readonly translate: TranslateService,
    clinicService: ClinicService,
  ) {
    this.tz = getTimeZone(clinicService.clinic)
  }

  ngOnInit(): void {
    this.loadingSubject.next(true)
    this.errorSubject.next(false)
    // shareReplay so an additional async-pipe subscriber (e.g. a future
    // emptyText binding) does not double-fire the HTTP call or the
    // loading/error side effects in `catchError`/`finalize`.
    this.history$ = this.api.getHistory(this.data.estimateId).pipe(
      // Drop systemActionLogger duplicates: every balance lifecycle event is logged
      // twice — once by logEstimateHistory (clinician actor, typed) and once by
      // systemActionLogger.info (patientpal actor, type='none'). The latter is noise
      // in the patient-facing view.
      map(logs => logs.filter(log => log.type !== LOG_TYPE.NONE)),
      map(logs => this.groupByDay(logs)),
      catchError(() => {
        this.errorSubject.next(true)
        return of<HistoryDay<EstimateHistoryModel>[]>([])
      }),
      finalize(() => this.loadingSubject.next(false)),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
  }

  ngOnDestroy(): void {
    this.loadingSubject.complete()
    this.errorSubject.complete()
  }

  describe(log: EstimateHistoryModel): string {
    return describeEstimateHistoryEntry(log, {
      formatMoney: (n) => this.currency.transform(n, 'USD') ?? '',
      actorName: log.actorName,
      typeName: getLogTypeName(log.type as LOG_TYPE),
      t: (key, params) => this.translate.instant(key, params),
    })
  }

  private groupByDay(logs: EstimateHistoryModel[]): HistoryDay<EstimateHistoryModel>[] {
    const grouped = groupBy(logs, log => moment.tz(log.createdAt, this.tz).format('YYYY-MM-DD'))
    return Object.entries(grouped).map(([date, dayLogs]) => ({
      date: moment.tz(date, this.tz),
      logs: dayLogs,
    }))
  }
}
